// The merchant application the join page collects, and its client-side checks. The API validates
// again; these only decide which fields to flag before submitting.

export type Locale = 'ar' | 'en';
export type BranchCount = '1' | '2-5' | '6+';
export const DISCOUNTS = [10, 15, 20, 25] as const;
export type Discount = (typeof DISCOUNTS)[number];

export interface GeoPoint {
  lat: number;
  lng: number;
}

export interface ApplicationForm {
  nameAr: string;
  categoryId: string;
  branchCount: BranchCount;
  discount: Discount | null;
  location: GeoPoint | null;
  contactName: string;
  contactMobile: string;
  contactEmail: string;
  consent: boolean;
  /** Honeypot: hidden from people, filled by bots. A non-empty value drops the submission. */
  website: string;
}

export const emptyForm: ApplicationForm = {
  nameAr: '',
  categoryId: '',
  branchCount: '1',
  discount: null,
  location: null,
  contactName: '',
  contactMobile: '',
  contactEmail: '',
  consent: false,
  website: '',
};


/** Local-preview categories (no API): the same ten as the member app's rail. Live, the list comes from GET /v1/public/categories. */
export const CATEGORIES: { key: string; ar: string; en: string }[] = [
  { key: 'dining', en: 'Restaurants & cafés', ar: 'مطاعم ومقاهي' },
  { key: 'retail', en: 'Retail & fashion', ar: 'تسوق وأزياء' },
  { key: 'beauty', en: 'Health & beauty', ar: 'صحة وتجميل' },
  { key: 'gyms', en: 'Gyms', ar: 'نوادي رياضية' },
  { key: 'hotels', en: 'Hotels & travel', ar: 'فنادق وسفر' },
  { key: 'auto', en: 'Auto services', ar: 'خدمات السيارات' },
  { key: 'entertainment', en: 'Entertainment & events', ar: 'ترفيه وفعاليات' },
  { key: 'vip', en: 'VIP services', ar: 'خدمات كبار الشخصيات' },
  { key: 'education', en: 'Education & training', ar: 'التعليم والتدريب' },
  { key: 'home', en: 'Home & furniture', ar: 'المنزل والأثاث' },
];

const ARABIC_DIGITS = /[٠-٩۰-۹]/g;

/** Arabic-Indic and Persian digits to ASCII, so "٠٧٩٠…" validates like "0790…". */
export function toAsciiDigits(s: string): string {
  return s.replace(ARABIC_DIGITS, (d) => String((d.charCodeAt(0) & 0xf) % 10));
}

/**
 * An Iraqi mobile as E.164 (+9647XXXXXXXXX), or null. Accepts 07XX…, 7XX…, 9647XX…, +9647XX…
 * and 009647XX…, with spaces or dashes.
 */
export function normalizeIraqiMobile(input: string): string | null {
  let d = toAsciiDigits(input).replace(/[\s\-().]/g, '');
  if (d.startsWith('+')) d = d.slice(1);
  if (d.startsWith('00')) d = d.slice(2);
  if (d.startsWith('964')) d = d.slice(3);
  if (d.startsWith('0')) d = d.slice(1);
  return /^7\d{9}$/.test(d) ? `+964${d}` : null;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export type FieldKey = keyof ApplicationForm;
export type ErrorCode = 'required' | 'invalidMobile' | 'invalidEmail' | 'arabicName';

const HAS_ARABIC = /[؀-ۿ]/;

export function validate(f: ApplicationForm): Partial<Record<FieldKey, ErrorCode>> {
  const e: Partial<Record<FieldKey, ErrorCode>> = {};
  if (!f.nameAr.trim()) e.nameAr = 'required';
  else if (!HAS_ARABIC.test(f.nameAr)) e.nameAr = 'arabicName';
  if (!f.categoryId) e.categoryId = 'required';
  if (f.discount == null) e.discount = 'required';
  if (!f.location) e.location = 'required';
  if (!f.contactName.trim()) e.contactName = 'required';
  if (!f.contactMobile.trim()) e.contactMobile = 'required';
  else if (!normalizeIraqiMobile(f.contactMobile)) e.contactMobile = 'invalidMobile';
  if (f.contactEmail.trim() && !EMAIL.test(f.contactEmail.trim())) e.contactEmail = 'invalidEmail';
  if (!f.consent) e.consent = 'required';
  return e;
}

/** Field order on the page, so the first error can be scrolled to. */
export const FIELD_ORDER: FieldKey[] = ['nameAr', 'categoryId', 'branchCount', 'discount', 'location', 'contactName', 'contactMobile', 'contactEmail', 'consent'];

/** The request body for POST /v1/public/partner-applications (contract PartnerApplicationInput). */
export function toPayload(f: ApplicationForm, locale: Locale) {
  const email = f.contactEmail.trim().toLowerCase();
  return {
    businessName: f.nameAr.trim(),
    categoryId: f.categoryId,
    location: f.location!,
    branchCount: f.branchCount,
    discountPct: f.discount!,
    contactName: f.contactName.trim(),
    phone: normalizeIraqiMobile(f.contactMobile)!,
    email: email || null,
    locale,
    website: f.website,
  };
}
